import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDrag } from 'react-dnd';
import cx from 'classnames';
import mobile from 'is-mobile';
import { toast } from 'react-toastify';
import useOutsideClick from '../../../hook/useOutsideClick';
import { usePermission } from '../../../container/permissionContext';
import { updateForm } from '../../../services/form';
import { copyCorporateLibraryFormToGeneralLibrary } from '../../../services/libraries/corporate-library';
import { isResponseOk } from '../../../utility/isResponseOk';
import CopyToGeneralLibraryModal from '../../../components/library/form-item/components/copy-to-general-library-modal';
import { DND_TYPE_TASK_FORM, getDropTargetFolderId } from '../constants';

/**
 * A form placed on the Tasks page.
 *
 * The row behaves the way the corporate library one does -- clicking it opens the
 * form's initiation, and the menu offers the same two actions -- but it is moved
 * with `task_folder_id`, because the form lives in the tasks tree rather than in
 * the library one.
 */
const TaskFormItem = ({ form, hasWritePermissions, handleMove, handleMoveError, openFormManagementModal }) => {
  const navigate = useNavigate();
  const { isSuperAdmin } = usePermission();
  const menuButtonWrapperRef = useRef(null);
  const menuRef = useRef(null);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ left: 0, right: 'initial' });
  const [isOpenCopyModal, setIsOpenCopyModal] = useState(false);

  useOutsideClick(menuButtonWrapperRef, () => setIsDropdownOpen(false));

  const [, drag] = useDrag(
    () => ({
      type: DND_TYPE_TASK_FORM,
      item: { item: form },
      canDrag: hasWritePermissions,
      end: (draggedItem, monitor) => {
        const dropResult = monitor.getDropResult();

        if (!dropResult?.item) {
          return;
        }

        const targetFolderId = getDropTargetFolderId(dropResult.item);

        // The form already sits there - nothing to move.
        if (targetFolderId === (draggedItem.item.task_folder_id ?? null)) {
          return;
        }

        updateForm(draggedItem.item.id, { task_folder_id: targetFolderId, placement: 'tasks' })
          .then(() => handleMove(targetFolderId))
          .catch((e) => {
            console.log(e);
            handleMoveError(true);
          });
      },
    }),
    [form, hasWritePermissions, handleMove, handleMoveError],
  );

  useEffect(() => {
    const menuButton = menuButtonWrapperRef.current;
    if (menuButton && isDropdownOpen && menuRef.current) {
      const menuButtonRight = menuButton.getBoundingClientRect().right;

      // 300 - menu width
      if (menuButtonRight + 300 > window.innerWidth) {
        setMenuPosition({ left: 'initial', right: 0 });
      } else {
        setMenuPosition({ left: 0, right: 'initial' });
      }
    }
  }, [isDropdownOpen]);

  // A form whose initiator still has choices to make cannot be initiated from a
  // phone, the same rule the corporate library row applies.
  const { recipient_settings, submission_settings, can_manage_forms } = form;
  const isDisabledMobile =
    recipient_settings?.can_initiator_select_recipients ||
    submission_settings?.can_initiator_select_submitters ||
    submission_settings?.can_initiator_specify_period_availability_and_frequency ||
    submission_settings?.filling_type === 'PUBLIC';

  const handleClick = () => {
    if ((mobile() || mobile({ tablet: true, featureDetect: true })) && isDisabledMobile) {
      return;
    }

    navigate(`/initialization-form/${form.id}`);
  };

  const handleCopyToGeneralLibrary = async (folderID) => {
    try {
      const response = await copyCorporateLibraryFormToGeneralLibrary({ formID: form.id, targetFolderID: folderID });

      if (isResponseOk(response)) {
        setIsOpenCopyModal(false);
        toast.success('Form was successfully copied');
      }
    } catch (e) {
      console.log(e);
    }
  };

  return (
    <>
      <div ref={drag} className="library-item list-item cursor-pointer tasks-row" onClick={handleClick}>
        <div className="library-item-text tasks-row-main">
          <img className="tasks-row-icon" src="/images/file-big-icon.png" alt="" />
          <span className="tasks-row-name">{form.name}</span>
        </div>

        {can_manage_forms && (
          <div ref={menuButtonWrapperRef} className={cx('library-item-button', { 'library-item-button-open': isDropdownOpen })}>
            <button
              className="btn btn-outline btn-small library-item-menu-button"
              onClick={(e) => {
                e.stopPropagation();
                setIsDropdownOpen(!isDropdownOpen);
              }}
            >
              <img src="/images/more-vertical.svg" alt="" />
            </button>
            {isDropdownOpen && (
              <div className="library-item-menu" ref={menuRef} style={{ left: menuPosition.left, right: menuPosition.right }}>
                {openFormManagementModal && (
                  <button
                    className="btn btn-full"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsDropdownOpen(false);
                      openFormManagementModal(form);
                    }}
                  >
                    <i className="icon-settings mr-2" />
                    Form management
                  </button>
                )}
                {isSuperAdmin() && (
                  <button
                    className="btn btn-full"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsDropdownOpen(false);
                      setIsOpenCopyModal(true);
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M3.5 11.8333H2.66667C2.22464 11.8333 1.80072 11.6577 1.48816 11.3452C1.17559 11.0326 1 10.6087 1 10.1667V2.66667C1 2.22464 1.17559 1.80072 1.48816 1.48816C1.80072 1.17559 2.22464 1 2.66667 1H10.1667C10.6087 1 11.0326 1.17559 11.3452 1.48816C11.6577 1.80072 11.8333 2.22464 11.8333 2.66667V3.5M8.5 6.83333H16C16.9205 6.83333 17.6667 7.57953 17.6667 8.5V16C17.6667 16.9205 16.9205 17.6667 16 17.6667H8.5C7.57953 17.6667 6.83333 16.9205 6.83333 16V8.5C6.83333 7.57953 7.57953 6.83333 8.5 6.83333Z"
                        stroke="#101828"
                        strokeWidth="1.67"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <span className="ml-2">Copy to the General Library</span>
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* A sibling of the row, not a child: a click inside the modal would
          otherwise bubble up to the row and open the form's initiation. */}
      {isOpenCopyModal && <CopyToGeneralLibraryModal onClose={() => setIsOpenCopyModal(false)} onCopy={handleCopyToGeneralLibrary} />}
    </>
  );
};

export default TaskFormItem;
